import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api, { getLeadsWithRequirements } from '../../../../services/api';

/**
 * Custom hook to fetch leads with filtering
 * @param {Object} filters - Filter parameters
 * @param {string} filters.searchQuery - Search text
 * @param {string} filters.callFilter - Call filter type
 * @param {string} filters.tagFilter - Tag filter
 * @param {string} filters.assigneeFilter - Assignee filter
 * @param {boolean} filters.missedFollowUpFilter - Filter for missed follow-ups
 * @param {boolean} filters.todayFollowUpFilter - Filter for today's follow-ups
 * @param {Object} filters.dateFilter - Date range filter
 * @param {Object} options - Additional query options
 * @returns {Object} - Query result with leads data
 */
export const useLeads = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: async () => {
      // Use the getLeadsWithRequirements endpoint as it includes stages and other required data
      const response = await getLeadsWithRequirements({
        ...filters,
        limit: 100,
      });
      console.log('Loaded leads data:', response.data);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Custom hook to fetch leads with pagination using infinite query
 * @param {Object} filters - Filter parameters
 * @param {Object} options - Additional infinite query options
 * @returns {Object} - Infinite query result with paginated leads data
 */
export const useInfiniteLeads = (filters = {}, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['infiniteLeads', filters],
    queryFn: async ({ pageParam = 1 }) => {
      console.log(`Fetching leads page ${pageParam}`);
      
      // Use the getLeadsWithRequirements endpoint as it includes stages and other required data
      const response = await getLeadsWithRequirements({
        ...filters,
        page: pageParam,
        limit: 50, // Fixed page size
      });
      
      // Normalize the API response to ensure consistent format
      const normalizedResponse = {
        leads: response.data?.leads || [],
        stages: response.data?.stages || [],
        pagination: response.data?.pagination || { 
          currentPage: pageParam,
          pages: 1,
          total: response.data?.leads?.length || 0 
        }
      };
      
      // Debug the API response
      console.log(`Page ${pageParam} response:`, {
        leadCount: normalizedResponse.leads.length,
        hasStages: Boolean(normalizedResponse.stages.length),
        pagination: normalizedResponse.pagination,
      });
      
      return normalizedResponse;
    },
    getNextPageParam: (lastPage) => {
      // Extract pagination info safely
      const pagination = lastPage?.pagination || {};
      const currentPage = pagination.currentPage || 1;
      const totalPages = pagination.pages || 1;
      
      console.log('Pagination info:', { currentPage, totalPages });
      
      // Return the next page number if there are more pages
      if (currentPage < totalPages) {
        console.log(`Next page will be ${currentPage + 1}`);
        return currentPage + 1;
      }
      
      console.log('No more pages available');
      return undefined;
    },
    // Ensure we get all pages
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Custom hook to fetch a single lead by ID
 * @param {string} leadId - Lead ID
 * @param {Object} options - Additional query options
 * @returns {Object} - Query result with lead data
 */
export const useLead = (leadId, options = {}) => {
  return useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      const response = await api.get(`/api/leads/${leadId}`);
      return response.data;
    },
    enabled: !!leadId,
    ...options,
  });
};

/**
 * Custom hook to update a lead's stage
 * @returns {Object} - Mutation object for updating lead stage
 */
export const useUpdateLeadStage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ leadId, newStage }) => {
      const response = await api.put(`/api/leads/${leadId}`, { stage: newStage });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
    },
  });
};

/**
 * Custom hook to update a lead
 * @returns {Object} - Mutation object for updating lead
 */
export const useUpdateLead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadData) => {
      const response = await api.put(`/api/leads/${leadData.leadId}`, leadData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
    },
  });
};

/**
 * Custom hook to add a new lead
 * @returns {Object} - Mutation object for adding lead
 */
export const useAddLead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadData) => {
      const response = await api.post('/api/leads', leadData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
    },
  });
};

/**
 * Combined hook that provides all lead query functionality
 * @param {Object} filters - Filters to apply to lead queries
 * @returns {Object} - Combined query and mutation objects
 */
export const useLeadQueries = (filters = {}) => {
  const { 
    data: leadsData,
    isLoading,
    error,
    refetch
  } = useLeads(filters);
  
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching
  } = useInfiniteLeads(filters, { enabled: true }); // Enable infinite query by default
  
  const updateLeadStageMutation = useUpdateLeadStage();
  const updateLeadMutation = useUpdateLead();
  const addLeadMutation = useAddLead();
  
  const [isLoadingAllLeads, setIsLoadingAllLeads] = useState(false);
  
  // Extract leads and stages from the data
  // Combine leads from both the regular query and infinite query
  const leads = useMemo(() => {
    // First try to get leads from infinite query data
    if (infiniteData?.pages && infiniteData.pages.length > 0) {
      // Flatten all pages and extract the leads - handle different response formats
      const allLeads = infiniteData.pages.flatMap(page => {
        // Handle different API response structures
        if (page.leads && Array.isArray(page.leads)) {
          return page.leads;
        } else if (page.data && Array.isArray(page.data)) {
          return page.data;
        } else {
          return [];
        }
      });
      
      console.log(`Got ${allLeads.length} leads from ${infiniteData.pages.length} pages`);
      
      // Enhanced deduplication by ID to prevent duplicates across pages
      const uniqueLeadsMap = new Map();
      allLeads.forEach(lead => {
        // Handle different lead ID formats
        const id = lead.leadId || lead._id || lead.id;
        if (id) {
          uniqueLeadsMap.set(id, lead);
        }
      });
      
      const uniqueLeads = Array.from(uniqueLeadsMap.values());
      console.log(`After deduplication: ${uniqueLeads.length} unique leads`);
      return uniqueLeads;
    }
    
    // Fallback to regular query data if infinite query hasn't loaded
    const regularLeads = leadsData?.leads || [];
    console.log(`Using regular query data with ${regularLeads.length} leads`);
    return regularLeads;
  }, [infiniteData, leadsData]);
  
  // Fallback for different API formats
  const stagesFromData = leadsData?.stages || [];
  
  // If stages aren't directly in the response, try to extract them from the first lead
  const stages = stagesFromData.length > 0 ? stagesFromData : 
    (leads.length > 0 && leads[0]?.availableStages ? 
      leads[0].availableStages : []);
  
  // Load more leads function for pagination
  const loadMoreLeads = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      return fetchNextPage();
    }
    return Promise.resolve();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Load all leads function
  const loadAllLeads = useCallback(async () => {
    setIsLoadingAllLeads(true);
    try {
      console.log('Starting to load all leads...');
      const maxAttempts = 20; // Safety limit to avoid infinite loops
      let currentPage = 1;
      let hasMorePages = true;
      let consecutiveErrorCount = 0;
      const maxConsecutiveErrors = 3;
      
      // First, ensure we have the initial data
      await refetch();
      
      while (hasMorePages && currentPage <= maxAttempts && consecutiveErrorCount < maxConsecutiveErrors) {
        try {
          console.log(`Loading page ${currentPage}...`);
          const result = await fetchNextPage();
          
          // Check if there are more pages to load
          hasMorePages = result.hasNextPage;
          
          // Log the result from loading the page
          const pageData = result.data?.pages?.[result.data.pages.length-1];
          console.log(`Loaded page ${currentPage} with ${pageData?.leads?.length || 0} leads`);
          
          // Reset error count on success
          consecutiveErrorCount = 0;
          
          // Small delay to avoid overwhelming the server
          if (hasMorePages) {
            await new Promise(resolve => setTimeout(resolve, 300));
            currentPage++;
          }
        } catch (error) {
          console.error(`Error loading page ${currentPage}:`, error);
          consecutiveErrorCount++;
          if (consecutiveErrorCount < maxConsecutiveErrors) {
            // Try again after a delay
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      if (consecutiveErrorCount >= maxConsecutiveErrors) {
        console.warn(`Stopped loading after ${consecutiveErrorCount} consecutive errors`);
      }
      
      console.log(`Finished loading leads: loaded ${currentPage} pages, total leads: ${leads.length}`);
    } catch (error) {
      console.error('Error in loadAllLeads:', error);
    } finally {
      setIsLoadingAllLeads(false);
    }
  }, [refetch, fetchNextPage, leads, setIsLoadingAllLeads]);
  
  // Update lead stage
  const updateLeadStage = useCallback((leadId, newStage) => {
    return updateLeadStageMutation.mutate({ leadId, newStage });
  }, [updateLeadStageMutation]);
  
  // Update lead
  const updateLead = useCallback((leadData) => {
    return updateLeadMutation.mutate(leadData);
  }, [updateLeadMutation]);
  
  // Add new lead
  const addLead = useCallback((leadData) => {
    return addLeadMutation.mutate(leadData);
  }, [addLeadMutation]);
  
  return {
    leads,
    leadsData, // Add the original API response data
    stages,
    isLoading: isLoading || isFetching,
    isLoadingAllLeads,
    hasMore: hasNextPage,
    isFetchingNextPage, // Expose this for UI feedback
    loadMoreLeads,
    loadAllLeads,
    updateLeadStage,
    updateLead,
    isUpdatingLead: updateLeadMutation.isPending,
    addLead,
    isAddingLead: addLeadMutation.isPending,
    error
  };
};

// Utility function to extract stages from the leads data
export const extractStages = (leadsData) => {
  if (!leadsData) return [];
  
  // Try to get stages from different possible locations in the API response
  const stages = leadsData?.stages || [];
  console.log('Extracted stages:', stages);
  
  if (stages.length === 0) {
    // If no stages were found, use stages from the first lead if available
    const firstLead = leadsData?.leads?.[0];
    if (firstLead?.availableStages) {
      console.log('Using availableStages from first lead:', firstLead.availableStages);
      return firstLead.availableStages;
    }
  }
  
  return stages;
};
